'use client';

import React, { useEffect, useState } from 'react';
import { Wifi, WifiOff, RefreshCw, CloudOff } from 'lucide-react';
import { isOnline, onConnectionChange, getOfflineQueue, timeSinceLastSync } from '@/lib/offline';

export function ConnectionStatus() {
  const [online, setOnline] = useState(true);
  const [showBanner, setShowBanner] = useState(false);
  const [queueCount, setQueueCount] = useState(0);
  const [lastSync, setLastSync] = useState('');

  useEffect(() => {
    setOnline(isOnline());
    setQueueCount(getOfflineQueue().length);
    setLastSync(timeSinceLastSync());

    const cleanup = onConnectionChange((status) => {
      setOnline(status);
      setShowBanner(!status);
      if (status) {
        // Reconnected - refresh queue count
        setQueueCount(getOfflineQueue().length);
        // Auto-hide banner after 3s when back online
        setTimeout(() => setShowBanner(false), 3000);
      }
    });

    // Check periodically
    const interval = setInterval(() => {
      setQueueCount(getOfflineQueue().length);
      setLastSync(timeSinceLastSync());
    }, 30000);

    return () => {
      cleanup();
      clearInterval(interval);
    };
  }, []);

  // Show offline banner
  if (!online) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
        <div className="flex items-center gap-3 px-4 py-3 bg-red-600 text-white rounded-xl shadow-lg">
          <WifiOff className="w-4 h-4 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Sem conexão com a internet</p>
            <p className="text-xs opacity-80">As alterações serão salvas localmente e sincronizadas quando a conexão voltar.</p>
          </div>
          {queueCount > 0 && (
            <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs font-bold">
              {queueCount} pendente{queueCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>
    );
  }

  // Show reconnected banner briefly
  if (showBanner && online) {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4">
        <div className="flex items-center gap-3 px-4 py-3 bg-green-600 text-white rounded-xl shadow-lg">
          <Wifi className="w-4 h-4 shrink-0" />
          <p className="text-sm font-semibold">Conexão restabelecida!</p>
        </div>
      </div>
    );
  }

  return null;
}

// Compact inline indicator for use inside pages
export function ConnectionIndicator() {
  const [online, setOnline] = useState(true);

  useEffect(() => {
    setOnline(isOnline());
    const cleanup = onConnectionChange(setOnline);
    return cleanup;
  }, []);

  return (
    <div className="flex items-center gap-1.5">
      {online ? (
        <>
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className="text-xs text-gray-500">Online</span>
        </>
      ) : (
        <>
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-xs text-red-600 font-medium">Offline</span>
        </>
      )}
    </div>
  );
}
